// @ts-nocheck
/**
 * toukiProcessing.ts
 * 登記簿PDF処理・地番ハイライト関連メソッド
 * App.vue から切り出し
 */

import * as Layers from '@/js/layers'
import {
  highlightSpecificFeatures2025,
  highlightSpecificFeaturesCity,
} from '@/js/downLoad'

type VM = any

export function highlightToukiPolygon(vm: VM, feature: any): void {
  const map = vm.$store.state.map01
  if (!map || !feature || !feature.geometry) return

  // 既存のハイライトをクリア
  clearToukiHighlight(vm)

  // 筆ID_地図名 形式のキーを作成
  const fudeId = feature.properties?.['筆ID'] || ''
  const chizumei = feature.properties?.['地図名'] || ''
  const targetId = `${fudeId}_${chizumei}`

  // highlightedChibansに追加
  vm.$store.state.highlightedChibans.add(targetId)
  // lastHighlightedChibanも設定（最後クリック色で表示）
  vm.$store.state.lastHighlightedChiban = targetId

  // 既存のハイライト関数を呼び出し
  highlightSpecificFeatures2025(map, 'oh-homusyo-2025-polygon')

  // ポリゴンの中心座標を計算してパン
  const coords = feature.geometry.coordinates
  let allPoints = []
  if (feature.geometry.type === 'Polygon') {
    allPoints = coords[0] // 外周のみ
  } else if (feature.geometry.type === 'MultiPolygon') {
    coords.forEach(poly => allPoints.push(...poly[0]))
  }

  if (allPoints.length > 0) {
    // バウンディングボックスの中心を計算
    let minLng = Infinity, maxLng = -Infinity
    let minLat = Infinity, maxLat = -Infinity
    allPoints.forEach(([lng, lat]) => {
      minLng = Math.min(minLng, lng)
      maxLng = Math.max(maxLng, lng)
      minLat = Math.min(minLat, lat)
      maxLat = Math.max(maxLat, lat)
    })
    const centerLng = (minLng + maxLng) / 2
    const centerLat = (minLat + maxLat) / 2

    // 少し遅延してからパン（ハイライト表示が確定してから）
    setTimeout(() => {
      map.panTo([centerLng, centerLat], { duration: 500 })
    }, 100)
  }

}

/**
 * 登記簿ハイライトをクリア（既存のOH3ハイライトシステムを使用）
 */
export function clearToukiHighlight(vm: VM): void {
  const map = vm.$store.state.map01
  if (!map) return

  // highlightedChibansをクリア
  vm.$store.state.highlightedChibans.clear()
  vm.$store.state.lastHighlightedChiban = null

  // ハイライト表示を更新
  highlightSpecificFeatures2025(map, 'oh-homusyo-2025-polygon')
}

/**
 * 登記簿PDF一括抽出 → CSV出力
 */
export async function handleToukiBatchExtract(vm: VM, files: any): Promise<void> {
  const { parseMultipleToukiPdfsNoAI, toukiInfoToCsv, downloadCsv } = await import('@/js/toukiPdfParser')
  const { showError } = await import('@/js/utils/messageHelper')

  if (!files || files.length === 0) {
    showError('PDFファイルが選択されていません')
    return
  }

  // PDF以外のファイルを除外
  const pdfFiles = files.filter(f => f.name.toLowerCase().endsWith('.pdf'))
  if (pdfFiles.length === 0) {
    showError('PDFファイルがありません')
    return
  }

  // ダイアログ表示
  vm.toukiBatchProgress = { current: 0, total: pdfFiles.length, fileName: '' }
  vm.toukiBatchDialog = true

  try {
    // 進捗コールバック
    const onProgress = (current, total, fileName) => {
      vm.toukiBatchProgress = { current, total, fileName }
    }

    // 複数PDFを一括解析（AIなし・純正規表現版）
    const results = await parseMultipleToukiPdfsNoAI(pdfFiles, onProgress)

    // ダイアログを閉じる
    vm.toukiBatchDialog = false

    // CSV生成してダウンロード
    const csv = toukiInfoToCsv(results)
    const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '')
    downloadCsv(csv, `touki_${timestamp}.csv`)
  } catch (error) {
    vm.toukiBatchDialog = false
    console.error('[ToukiBatch] Error:', error)
    showError(`エラーが発生しました: ${error.message || error}`)
  }
}

/**
 * 登記簿PDF統合フロー
 * ハイライト + CSV出力 + 地図ジャンプ
 */
export async function handleToukiAll(vm: VM, files: any): Promise<void> {
  const { parseMultipleToukiPdfsNoAI, normalizeAddressForGeocoding, toukiInfoToCsv, downloadCsv } = await import('@/js/toukiPdfParser')
  const { showInfo, showError } = await import('@/js/utils/messageHelper')

  if (!files || files.length === 0) {
    showError('PDFファイルが選択されていません')
    return
  }

  // PDF以外のファイルを除外
  const pdfFiles = files.filter(f => f.name.toLowerCase().endsWith('.pdf'))
  if (pdfFiles.length === 0) {
    showError('PDFファイルがありません')
    return
  }

  const map = vm.$store.state.map01
  if (!map) {
    showError('地図が初期化されていません')
    return
  }

  // 進捗ダイアログ表示
  vm.toukiBatchProgress = { current: 0, total: pdfFiles.length, fileName: '' }
  vm.toukiBatchDialog = true

  try {
    // 1. 複数PDFを解析して地番リストを取得
    const onProgress = (current, total, fileName) => {
      vm.toukiBatchProgress = { current, total, fileName }
    }
    const results = await parseMultipleToukiPdfsNoAI(pdfFiles, onProgress)

    // 成功した結果のみフィルタ
    const successResults = results.filter(r => r.success && r.chiban)
    if (successResults.length === 0) {
      vm.toukiBatchDialog = false
      showError('地番を抽出できたPDFがありませんでした')
      return
    }

    // 所在ごとにグループ化（異なる市町村のPDFに対応）
    const shozaiGroups = new Map()
    for (const r of successResults) {
      const key = r.shozai
      if (!shozaiGroups.has(key)) {
        shozaiGroups.set(key, [])
      }
      shozaiGroups.get(key).push(r.chiban)
    }

    // 2. 2025登記所地図ソースを追加（なければ）
    const sourceId = 'homusyo-2025-source'
    const sourceDef = {
      type: 'vector',
      url: 'pmtiles://https://kenzkenz.net/pmtiles/2025final3.pmtiles',
    }
    if (!map.getSource(sourceId)) {
      map.addSource(sourceId, sourceDef)
    }

    // 一時レイヤーを追加
    const tempLayerId = 'temp-touki-batch-highlight'
    if (!map.getLayer(tempLayerId)) {
      map.addLayer({
        id: tempLayerId,
        type: 'fill',
        source: sourceId,
        'source-layer': 'parcels',
        paint: { 'fill-opacity': 0 },
      })
    }

    // 3. 各所在ごとにジオコーディング＆検索
    const matchedFeatures = []
    const geocodedLocations = [] // パン先候補
    const unmatchedChibans = [] // Pattern②用: 2025でマッチしなかった地番
    const searchedLayers = { has2025: true, cityNames: new Set() } // 検索したレイヤーを記録

    // ケ/ヶ/ｹ を統一する正規化関数（住所マッチング用）
    const normalizeKe = (str) => {
      if (!str) return ''
      return str
        .replace(/[ヶｹ]/g, 'ケ')  // 小さい「ヶ」と半角「ｹ」を大きい「ケ」に統一
    }

    let locationIndex = 0
    for (const [shozai, chibans] of shozaiGroups) {
      locationIndex++
      vm.toukiBatchProgress = {
        current: locationIndex,
        total: shozaiGroups.size,
        fileName: `${shozai} を検索中...`
      }

      let searchAddress = normalizeAddressForGeocoding(shozai)

      try {
        const gsiResponse = await fetch(
          `https://msearch.gsi.go.jp/address-search/AddressSearch?q=${encodeURIComponent(searchAddress)}`
        )
        const gsiData = await gsiResponse.json()

        if (!gsiData || gsiData.length === 0) {
          console.warn('[ToukiAll] Geocode failed for:', searchAddress)
          continue
        }

        const [lng, lat] = gsiData[0].geometry.coordinates
        geocodedLocations.push({ shozai, lng, lat, chibans })

        // 地図を移動（タイルロードのため）zoom14で広めに取得
        map.jumpTo({ center: [lng, lat], zoom: 14 })

        // アイドル状態を待つ
        await new Promise(resolve => {
          const onIdle = () => {
            map.off('idle', onIdle)
            resolve()
          }
          map.once('idle', onIdle)
          setTimeout(() => {
            map.off('idle', onIdle)
            resolve()
          }, 5000)
        })

        // レイヤーがレンダリングされるまで少し待つ
        await new Promise(resolve => setTimeout(resolve, 500))

        // 画面からフィーチャーを取得
        const features = map.queryRenderedFeatures({ layers: [tempLayerId] })

        // デバッグ: サンプル地番を出力
        const sample2025Chibans = features.slice(0, 10).map(f => f.properties?.地番)

        // この所在の地番をマッチング
        for (const chiban of chibans) {
          // 地番図によって「635番」「635」「635-1」など形式が異なるため複数パターンを用意
          const chibanPatterns = [
            chiban,                           // "635番" or "635番1"
            chiban.replace('番', '-'),        // "635-" or "635-1"
            chiban.replace(/番$/, ''),        // "635" (末尾の番を除去)
            chiban.replace(/番/, '')          // "6351" (番を除去、枝番ありの場合)
          ]
          // 地番が一致するフィーチャーをすべて取得
          const chibanMatches = features.filter(f => {
            const featureChiban = f.properties?.地番 || ''
            return chibanPatterns.some(p => featureChiban === p)
          })

          let matched = null

          // 所在から町名/大字名キーワードを抽出
          // 例: "大牟田市日出町2丁目" → ["日出町2丁目", "日出町二丁目"]（丁目指定時は厳密マッチ）
          // 例: "大牟田市新栄町" → ["新栄町", "新栄"]
          // 例: "大牟田市大字橘" → ["大字橘", "橘"]
          // 例: "浜名区細江町気賀字向山" → ["細江町気賀字向山", "細江町気賀"]（小字除去版も追加）
          const shozaiKeywords = []
          const fullMatch = shozai.match(/[市区郡]([^市区郡]+)$/)
          if (fullMatch && fullMatch[1]) {
            const townPart = fullMatch[1]
            shozaiKeywords.push(townPart)
            // 小字（字XXX）を除去したバージョンも追加
            if (townPart.includes('字') && !townPart.startsWith('大字')) {
              const withoutKoaza = townPart.replace(/字[^字]+$/, '')
              if (withoutKoaza && withoutKoaza !== townPart) {
                shozaiKeywords.push(withoutKoaza)
              }
            }
            const chomeNumMatch = townPart.match(/^(.+?)(\d+)(丁目)$/)
            if (chomeNumMatch) {
              const baseTown = chomeNumMatch[1]
              const num = parseInt(chomeNumMatch[2])
              const kanjiNums = ['', '一', '二', '三', '四', '五', '六', '七', '八', '九', '十']
              if (num <= 10) {
                shozaiKeywords.push(`${baseTown}${kanjiNums[num]}丁目`)
              }
              // 丁目番号付きの場合はフォールバックキーワードを追加しない
            } else {
              if (townPart.startsWith('大字')) {
                shozaiKeywords.push(townPart.replace('大字', ''))
              }
              if (townPart.endsWith('町')) {
                shozaiKeywords.push(townPart.replace(/町$/, ''))
              }
            }
          }


          if (chibanMatches.length === 1) {
            // 1件のみでも所在チェックを行う
            const f = chibanMatches[0]
            const oaza = f.properties?.大字名 || ''
            const chome = f.properties?.丁目名 || ''
            const muni = f.properties?.市区町村名 || ''
            const featureShozai = `${muni}${oaza}${chome}`

            // 所在キーワードがフィーチャーの住所に含まれているか（双方向で比較）
            // 例: kw="細江町気賀字向山" oaza="細江町気賀" → kw.includes(oaza) でマッチ
            // ケ/ヶ/ｹの違いを吸収するためnormalizeKeで正規化して比較
            const nFeatureShozai = normalizeKe(featureShozai)
            const nOaza = normalizeKe(oaza)
            const nChome = normalizeKe(chome)
            const shozaiMatch = shozaiKeywords.length === 0 || shozaiKeywords.some(kw => {
              const nKw = normalizeKe(kw)
              return nFeatureShozai.includes(nKw) || nOaza.includes(nKw) || nChome.includes(nKw) ||
                (nOaza && nKw.includes(nOaza))
            })
            if (shozaiMatch) {
              matched = f
            } else {
              // 所在不一致の場合はマッチしない（unmatchedに回す）
            }
          } else if (chibanMatches.length > 1) {
            // 複数ヒット：所在で絞り込む
            matched = chibanMatches.find(f => {
              const oaza = f.properties?.大字名 || ''
              const chome = f.properties?.丁目名 || ''
              const muni = f.properties?.市区町村名 || ''
              const featureShozai = `${muni}${oaza}${chome}`

              // 所在キーワードがフィーチャー住所に含まれるかチェック（双方向）
              // ケ/ヶ/ｹの違いを吸収するためnormalizeKeで正規化して比較
              const nFeatureShozai = normalizeKe(featureShozai)
              const nOaza = normalizeKe(oaza)
              const nChome = normalizeKe(chome)
              return shozaiKeywords.some(kw => {
                const nKw = normalizeKe(kw)
                return nFeatureShozai.includes(nKw) || nOaza.includes(nKw) || nChome.includes(nKw) ||
                  (nOaza && nKw.includes(nOaza))
              })
            })
            if (matched) {
            } else {
              // 所在でマッチしなければマッチなしとする（別の街の地番に誤ってハイライトしない）
            }
          }

          if (matched) {
            matchedFeatures.push({ chiban, shozai, feature: matched, source: '2025' })
          } else {
            // 2025でマッチしなかった地番を記録（Pattern②用）
            unmatchedChibans.push({ chiban, shozai, lng, lat })
          }
        }
      } catch (e) {
        console.error('[ToukiAll] Error at location:', shozai, e)
      }
    }


    // 一時レイヤーを削除
    if (map.getLayer(tempLayerId)) {
      map.removeLayer(tempLayerId)
    }

    // ========================================
    // Pattern②: 各都市地番図でのfallback検索
    // ========================================
    if (unmatchedChibans.length > 0) {
      vm.toukiBatchProgress = {
        current: 0,
        total: unmatchedChibans.length,
        fileName: '各都市地番図を検索中...'
      }

      // sicyosonChibanzuUrlsを取得（Layers経由）
      const sicyosonChibanzuUrls = Layers.sicyosonChibanzuUrls || []

      // MySQLから公開地番図データを取得
      let publicChibanzuData = []
      try {
        const pubResponse = await fetch('/oh3/php/userPmtileSelectPublicNew.php')
        if (pubResponse.ok) {
          publicChibanzuData = await pubResponse.json() || []
        }
      } catch (e) {
        console.warn('[ToukiAll] Failed to fetch public chibanzu data:', e)
      }

      // bbox判定関数
      // ジオコーディング座標がbbox境界近くの場合を考慮し、トレランスを設定
      // 0.05度 ≈ 約5km のマージンを許容
      // shozaiから市区町村名を抽出して優先的にマッチ
      const bboxTolerance = 0.05
      const findChibanzuByCoord = (lng, lat, shozai) => {
        // 所在から市名を抽出（例: "浜松市浜名区中瀬" → "浜松市"）
        const cityMatch = shozai.match(/^(.+?[市町村])/)
        const targetCityName = cityMatch ? cityMatch[1] : null

        const bboxMatches = []

        // sicyosonChibanzuUrlsから検索
        for (const city of sicyosonChibanzuUrls) {
          if (city.bbox) {
            const [west, south, east, north] = city.bbox
            if (lng >= west - bboxTolerance && lng <= east + bboxTolerance &&
                lat >= south - bboxTolerance && lat <= north + bboxTolerance) {
              bboxMatches.push({ type: 'sicyoson', data: city })
            }
          }
        }
        // 公開地番図から検索
        for (const pub of publicChibanzuData) {
          if (pub.bbox) {
            const bbox = typeof pub.bbox === 'string' ? JSON.parse(pub.bbox) : pub.bbox
            const [west, south, east, north] = bbox
            if (lng >= west - bboxTolerance && lng <= east + bboxTolerance &&
                lat >= south - bboxTolerance && lat <= north + bboxTolerance) {
              bboxMatches.push({ type: 'public', data: pub })
            }
          }
        }


        // 市名が一致するものを優先
        if (targetCityName && bboxMatches.length > 0) {
          const cityNameMatch = bboxMatches.find(m => {
            const name = m.data.name || ''
            // 公開地番図は "静岡県-浜松市" 形式、sicyosonは "浜松市" 形式
            return name.includes(targetCityName) || name.endsWith(targetCityName)
          })
          if (cityNameMatch) {
            return cityNameMatch
          }
        }

        // 市名マッチがなければ最初のbboxマッチを返す
        return bboxMatches.length > 0 ? bboxMatches[0] : null
      }

      // chibanプロパティから地番フィールド名を抽出する関数
      const getChibanFieldName = (chibanExpr) => {
        if (!chibanExpr) return null
        // 文字列の場合（MySQLデータ）- そのままフィールド名として使用
        if (typeof chibanExpr === 'string') {
          return chibanExpr
        }
        // ['get', 'フィールド名'] の形式
        if (Array.isArray(chibanExpr) && chibanExpr[0] === 'get') {
          return chibanExpr[1]
        }
        // ['concat', ['get', 'xxx'], '-', ['get', 'yyy']] の形式
        if (Array.isArray(chibanExpr) && chibanExpr[0] === 'concat') {
          // 最初の['get', 'xxx']を返す（簡易実装）
          const firstGet = chibanExpr.find(item => Array.isArray(item) && item[0] === 'get')
          return firstGet ? firstGet[1] : null
        }
        return null
      }

      // 各未マッチ地番に対してfallback検索
      for (let i = 0; i < unmatchedChibans.length; i++) {
        const { chiban, shozai, lng, lat } = unmatchedChibans[i]
        vm.toukiBatchProgress = {
          current: i + 1,
          total: unmatchedChibans.length,
          fileName: `${shozai} を各都市地番図で検索中...`
        }

        // bbox判定で該当都市を特定（shozaiから市名を抽出して優先）
        const matchedCity = findChibanzuByCoord(lng, lat, shozai)
        if (!matchedCity) {
          continue
        }

        const cityData = matchedCity.data
        const cityName = cityData.name
        searchedLayers.cityNames.add(cityName) // 検索した都市を記録

        // ソースIDとレイヤーIDを決定
        let sourceId, layerId, sourceUrl, sourceLayer
        if (matchedCity.type === 'sicyoson') {
          sourceId = `oh-chibanzu-${cityName}-source`
          layerId = `oh-chibanzu-${cityName}`
          sourceUrl = `pmtiles://https://kenzkenz.net/pmtiles/chiban/${cityData.url}.pmtiles`
          sourceLayer = cityName === '高崎市' ? 'oh3' : 'chibanzu'
        } else {
          // 公開地番図
          sourceId = `oh-chiban-${cityData.id}-${cityData.name}-source`
          layerId = `oh-chiban-${cityData.id}-${cityData.name}-layer`
          sourceUrl = 'pmtiles://' + cityData.url
          sourceLayer = 'oh3'
        }

        // ソースが存在しなければ追加
        if (!map.getSource(sourceId)) {
          map.addSource(sourceId, {
            type: 'vector',
            url: sourceUrl
          })
        }

        // 一時レイヤーを追加
        const tempCityLayerId = `temp-touki-city-${cityName}`
        if (!map.getLayer(tempCityLayerId)) {
          map.addLayer({
            id: tempCityLayerId,
            type: 'fill',
            source: sourceId,
            'source-layer': sourceLayer,
            paint: { 'fill-opacity': 0 }
          })
        }

        // 地図を移動してタイルをロード（zoom14で広めに取得）
        map.jumpTo({ center: [lng, lat], zoom: 14 })

        // アイドル状態を待つ
        await new Promise(resolve => {
          const onIdle = () => {
            map.off('idle', onIdle)
            resolve()
          }
          map.once('idle', onIdle)
          setTimeout(() => {
            map.off('idle', onIdle)
            resolve()
          }, 5000)
        })
        await new Promise(resolve => setTimeout(resolve, 500))

        // フィーチャーを取得
        const cityFeatures = map.queryRenderedFeatures({ layers: [tempCityLayerId] })

        // 地番フィールド名を取得
        const chibanFieldName = getChibanFieldName(cityData.chiban)

        // デバッグ: 最初の5件の地番値を出力
        const sampleChibans = cityFeatures.slice(0, 5).map(f => f.properties?.[chibanFieldName])

        // 地番マッチング
        // 地番図によって「635番」「635」「635-1」など形式が異なるため複数パターンを用意
        const chibanPatterns = [
          chiban,                           // "635番" or "635番1"
          chiban.replace('番', '-'),        // "635-" or "635-1"
          chiban.replace(/番$/, ''),        // "635" (末尾の番を除去)
          chiban.replace(/番/, '')          // "6351" (番を除去、枝番ありの場合)
        ]

        // 所在からキーワードを抽出（2025と同様）
        // 例: "大牟田市日出町2丁目" → ["日出町2丁目", "日出町二丁目"]（丁目指定時は厳密マッチ）
        // 例: "浜名区細江町気賀字向山" → ["細江町気賀字向山", "細江町気賀"]（小字除去版も追加）
        const shozaiKeywords = []
        const fullMatch = shozai.match(/[市区郡]([^市区郡]+)$/)
        if (fullMatch && fullMatch[1]) {
          const townPart = fullMatch[1]
          shozaiKeywords.push(townPart)
          // 小字（字XXX）を除去したバージョンも追加
          if (townPart.includes('字') && !townPart.startsWith('大字')) {
            const withoutKoaza = townPart.replace(/字[^字]+$/, '')
            if (withoutKoaza && withoutKoaza !== townPart) {
              shozaiKeywords.push(withoutKoaza)
            }
          }
          const chomeNumMatch = townPart.match(/^(.+?)(\d+)(丁目)$/)
          if (chomeNumMatch) {
            const baseTown = chomeNumMatch[1]
            const num = parseInt(chomeNumMatch[2])
            const kanjiNums = ['', '一', '二', '三', '四', '五', '六', '七', '八', '九', '十']
            if (num <= 10) {
              shozaiKeywords.push(`${baseTown}${kanjiNums[num]}丁目`)
            }
            // 丁目番号付きの場合はフォールバックキーワードを追加しない
          } else {
            if (townPart.startsWith('大字')) {
              shozaiKeywords.push(townPart.replace('大字', ''))
            }
            if (townPart.endsWith('町')) {
              shozaiKeywords.push(townPart.replace(/町$/, ''))
            }
          }
        }

        // 地番マッチ候補を収集
        const chibanMatches = []
        for (const f of cityFeatures) {
          const featureChiban = f.properties?.[chibanFieldName] || ''
          if (chibanPatterns.some(p => String(featureChiban) === p || String(featureChiban).includes(p))) {
            chibanMatches.push(f)
          }
        }


        let matchedFeature = null
        if (chibanMatches.length === 1) {
          // 1件のみでも所在チェック
          const f = chibanMatches[0]
          const props = f.properties || {}
          // 都市地番図の住所プロパティを取得（様々な形式に対応）
          const featureShozai = [
            props['大字名'] || props['大字'] || props['Ooaza'] || '',
            props['丁目名'] || props['丁目'] || '',
            props['町名'] || props['町'] || '',
            props['小字名'] || props['小字'] || '',
            props['地区名'] || ''
          ].join('')

          if (shozaiKeywords.length === 0) {
            // キーワードがない場合は地番のみでマッチ
            matchedFeature = f
          } else if (featureShozai && shozaiKeywords.some(kw => {
            // ケ/ヶ/ｹの違いを吸収するためnormalizeKeで正規化して比較
            const nFeatureShozai = normalizeKe(featureShozai)
            const nKw = normalizeKe(kw)
            return nFeatureShozai.includes(nKw) || nKw.includes(nFeatureShozai)
          })) {
            // キーワードがフィーチャーの住所に含まれる、または逆方向でもマッチ
            matchedFeature = f
          } else if (!featureShozai) {
            // フィーチャーに住所情報がない場合は地番のみでマッチ（大字フィールドがないレイヤー）
            matchedFeature = f
          } else {
          }
        } else if (chibanMatches.length > 1) {
          // 複数マッチ：所在で絞り込む（キーワードがフィーチャーの住所に含まれる場合のみ）
          // 大字フィールドがあるものを優先して検索
          // デバッグ: 候補の大字を表示

          matchedFeature = chibanMatches.find(f => {
            const props = f.properties || {}
            const oazaValue = props['大字名'] || props['大字'] || props['Ooaza'] || ''
            const featureShozai = [
              oazaValue,
              props['丁目名'] || props['丁目'] || '',
              props['町名'] || props['町'] || '',
              props['小字名'] || props['小字'] || '',
              props['地区名'] || ''
            ].join('')

            if (!featureShozai) return false // 住所情報がなければスキップ
            // ケ/ヶ/ｹの違いを吸収するためnormalizeKeで正規化して比較
            const nFeatureShozai = normalizeKe(featureShozai)
            const nOaza = normalizeKe(oazaValue)

            // 複数候補がある場合は厳密にマッチ（逆方向マッチは大字が十分長い場合のみ）
            return shozaiKeywords.some(kw => {
              const nKw = normalizeKe(kw)
              // フィーチャーの住所がキーワードを含む（正方向）
              if (nFeatureShozai.includes(nKw)) return true
              // キーワードが大字を含む（逆方向）- 大字が4文字以上の場合のみ
              if (nOaza.length >= 4 && nKw.includes(nOaza)) return true
              return false
            })
          })

          if (!matchedFeature && shozaiKeywords.length === 0) {
            // キーワードがない場合は最初のものを使用
            matchedFeature = chibanMatches[0]
          }

          // 大字フィールドがないレイヤーの場合：所在マッチできなくても最初のものを使う
          if (!matchedFeature) {
            const hasOazaField = chibanMatches.some(f => {
              const props = f.properties || {}
              return props['大字名'] || props['大字'] || props['Ooaza'] || props['丁目名'] || props['丁目'] || props['町名'] || props['町']
            })
            if (!hasOazaField) {
              // 大字フィールドがないレイヤー：地番のみでマッチ
              matchedFeature = chibanMatches[0]
            }
          }

          if (matchedFeature) {
            const mp = matchedFeature.properties || {}
          } else {
          }
        }

        if (matchedFeature) {
          matchedFeatures.push({
            chiban,
            shozai,
            feature: matchedFeature,
            source: 'city',
            cityName,
            layerId,
            chibanFieldName,
            cityData,
            sourceLayer,
            sourceId
          })
        }

        // 一時レイヤーを削除
        if (map.getLayer(tempCityLayerId)) {
          map.removeLayer(tempCityLayerId)
        }
      }

    }
    // ========================================
    // Pattern② ここまで
    // ========================================

    // 7. ハイライト処理
    // 既存のハイライトをクリア
    vm.$store.state.highlightedChibans.clear()
    vm.$store.state.highlightedChibansDetails.clear()
    vm.$store.state.lastHighlightedChiban = null

    // ポリゴンから中心座標を計算するヘルパー
    const calcCenter = (geometry) => {
      if (!geometry) return null
      const coords = geometry.coordinates
      let allPoints = []
      if (geometry.type === 'Polygon') {
        allPoints = coords[0]
      } else if (geometry.type === 'MultiPolygon') {
        coords.forEach(poly => allPoints.push(...poly[0]))
      }
      if (allPoints.length === 0) return null
      let minLng = Infinity, maxLng = -Infinity
      let minLat = Infinity, maxLat = -Infinity
      allPoints.forEach(([pLng, pLat]) => {
        minLng = Math.min(minLng, pLng)
        maxLng = Math.max(maxLng, pLng)
        minLat = Math.min(minLat, pLat)
        maxLat = Math.max(maxLat, pLat)
      })
      return [(minLng + maxLng) / 2, (minLat + maxLat) / 2]
    }

    // マッチしたfeatureをhighlightedChibans & highlightedChibansDetailsに追加
    let firstFeatureCenter = null
    const cityLayersToHighlight = new Set() // 各都市地番図のレイヤーID

    for (const matched of matchedFeatures) {
      const { feature, shozai, source, cityName, layerId: cityLayerId, chibanFieldName } = matched
      const props = feature.properties || {}
      const center = calcCenter(feature.geometry)

      let targetId, featureChiban, fullAddress

      if (source === '2025') {
        // 2025登記所地図の場合
        const fudeId = props['筆ID'] || ''
        featureChiban = props['地番'] || ''
        const oaza = props['大字名'] || ''
        const chome = props['丁目名'] || ''
        // 筆ID_地図名 形式のキー
        const featureChizumei = props['地図名'] || ''
        targetId = `${fudeId}_${featureChizumei}`
        const addressPart = (oaza + chome).trim()
        fullAddress = shozai
          ? `${shozai} ${featureChiban}`
          : (addressPart ? `${addressPart} ${featureChiban}` : featureChiban)

        vm.$store.state.highlightedChibans.add(targetId)
        vm.$store.state.highlightedChibansDetails.set(targetId, {
          fudeId,
          chiban: featureChiban,
          address: fullAddress,
          center,
          properties: props,
          source: '2025'
        })
      } else {
        // 各都市地番図の場合
        // highlightSpecificFeaturesCityはoh3idを使うので、oh3idを優先
        const featureId = props['oh3id'] || props['id'] || ''
        featureChiban = chibanFieldName ? (props[chibanFieldName] || '') : ''
        targetId = String(featureId)
        fullAddress = shozai
          ? `${shozai} ${featureChiban}`
          : `${cityName} ${featureChiban}`

        vm.$store.state.highlightedChibans.add(targetId)
        vm.$store.state.highlightedChibansDetails.set(targetId, {
          fudeId: '', // 各都市地番図には筆IDがない
          chiban: String(featureChiban),
          address: fullAddress,
          center,
          properties: props,
          source: 'city',
          cityName,
          layerId: cityLayerId
        })

        // ハイライト対象のレイヤーを記録
        if (cityLayerId) {
          cityLayersToHighlight.add(cityLayerId)
        }
      }

      // 最初のfeatureの中心座標を記録
      if (!firstFeatureCenter && center) {
        firstFeatureCenter = center
      }
    }

    // 8. 2025登記所地図レイヤーをONにする
    const layerId = 'oh-homusyo-2025-layer'
    const selectedLayers = vm.$store.state.selectedLayers.map01 || []
    const isLayerActive = selectedLayers.some(l => l.id === layerId)
    if (!isLayerActive) {
      const findLayerById = (tree, targetId) => {
        if (!tree) return null
        for (const node of tree) {
          if (node.id === targetId) return node
          if (node.nodes) {
            const found = findLayerById(node.nodes, targetId)
            if (found) return found
          }
          if (node.children) {
            const found = findLayerById(node.children, targetId)
            if (found) return found
          }
        }
        return null
      }
      const node = findLayerById(Layers.layers.map01, layerId)
      if (node) {
        const layerObj = {
          id: node.id,
          label: node.label,
          source: node.source,
          sources: node.sources,
          layer: node.layer,
          layers: node.layers,
          opacity: 1,
          visibility: true,
          attribution: node.attribution,
          ext: node.ext,
          info: node.info,
          position: node.position
        }
        vm.$store.commit('TOGGLE_SELECTED_LAYER', { mapName: 'map01', layer: layerObj })
      }
    }

    // 8.5. 都市地番図レイヤーをONにする（既存のレイヤーシステムを使用）
    const cityMatches = matchedFeatures.filter(m => m.source === 'city')
    const addedCityLayers = new Set()
    for (const matched of cityMatches) {
      const { cityData, cityName } = matched
      if (!cityData || !cityData.url || !cityData.id) continue

      // 既存システムと同じレイヤーID形式を使用
      const layerIdBase = `oh-chiban-${cityData.id}-${cityData.name}`
      const mainLayerId = `${layerIdBase}-layer`

      if (addedCityLayers.has(mainLayerId)) continue
      addedCityLayers.add(mainLayerId)

      // selectedLayersに既に存在するかチェック
      const selectedLayers = vm.$store.state.selectedLayers.map01 || []
      const isAlreadySelected = selectedLayers.some(l => l.id === mainLayerId)
      if (isAlreadySelected) {
        cityLayersToHighlight.add(mainLayerId)
        continue
      }

      // publiLayersCreateと同じ形式でレイヤーオブジェクトを作成
      // boundsをパース
      let bounds = null
      if (cityData.bbox) {
        try {
          const bboxArray = JSON.parse(cityData.bbox)
          if (Array.isArray(bboxArray)) {
            bboxArray.sort((a, b) => a - b)
            bounds = [bboxArray[2], bboxArray[0], bboxArray[3], bboxArray[1]]
          }
        } catch (e) {
          console.warn('[ToukiAll] Failed to parse bbox:', e)
        }
      }

      // ソース定義
      const sourceObj = {
        id: `${layerIdBase}-source`,
        obj: {
          type: 'vector',
          url: 'pmtiles://' + cityData.url,
        },
        minzoom: 13,
        bounds: bounds,
      }

      // line color（publicと同じロジック）
      let lineColor = 'blue'
      if (cityData.public === '-1') {
        lineColor = 'green'
      }

      // chibanフィールド名
      const chibanField = cityData.chiban || 'Chiban'

      // minZoom for label（publicと同じロジック）
      let labelMinZoom = 17
      if (cityData.length && cityData.length < 10000) {
        labelMinZoom = 0
      }

      // fill layer
      const fillLayer = {
        name: cityData.name,
        id: mainLayerId,
        type: 'fill',
        source: `${layerIdBase}-source`,
        'source-layer': 'oh3',
        paint: {
          'fill-color': 'rgba(0,0,0,0)',
        },
        minzoom: 13,
      }

      // line layer
      const lineLayer = {
        id: `oh-chibanL-${cityData.id}-${cityData.name}-line-layer`,
        source: `${layerIdBase}-source`,
        type: 'line',
        'source-layer': 'oh3',
        paint: {
          'line-color': lineColor,
          'line-width': ['interpolate', ['linear'], ['zoom'], 0, 0.1, 16, 2],
        },
        minzoom: 13,
      }

      // label layer
      const labelLayer = {
        id: `oh-chibanL-${cityData.id}-${cityData.name}-label-layer`,
        type: 'symbol',
        source: `${layerIdBase}-source`,
        'source-layer': 'oh3',
        layout: {
          'text-field': ['get', chibanField],
        },
        paint: {
          'text-color': 'navy',
          'text-halo-color': 'rgba(255,255,255,1)',
          'text-halo-width': 1.0,
        },
        minzoom: labelMinZoom,
      }

      // point layer（現在は非表示）
      const pointLayer = {
        id: `oh-chibanL-${cityData.id}-${cityData.name}-point-layer`,
        type: 'circle',
        source: `${layerIdBase}-source`,
        filter: ['==', '$type', 'Point'],
        'source-layer': 'oh3',
        layout: {
          visibility: 'none', // 常に非表示
        },
        paint: {
          'circle-color': 'rgba(0,0,255,1)',
          'circle-radius': 5,
          'circle-opacity': 1,
          'circle-stroke-width': 1,
          'circle-stroke-color': '#fff',
        },
        minzoom: 13,
      }

      // vertex layer（現在は非表示）
      const vertexLayer = {
        id: `oh-chibanL-${cityData.id}-${cityData.name}-vertex-layer`,
        type: 'circle',
        source: `${layerIdBase}-source`,
        filter: ['==', '$type', 'Polygon'],
        'source-layer': 'oh3',
        layout: {
          visibility: 'none', // 常に非表示
        },
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 15, 0, 18, 4],
          'circle-color': 'rgba(0,0,0,0)',
        },
        minzoom: 13,
      }

      // 既存システムと同じ形式のlayerObj
      const layerObj = {
        id: mainLayerId,
        label: cityData.name,
        source: sourceObj,
        layers: [fillLayer, lineLayer, labelLayer],
        opacity: 1,
        visibility: true,
        ext: { name: 'ext-chibanzu' },
      }

      vm.$store.commit('TOGGLE_SELECTED_LAYER', { mapName: 'map01', layer: layerObj })

      // cityLayersToHighlightを更新
      cityLayersToHighlight.add(mainLayerId)
    }

    // 9. ハイライト表示を更新
    // 2025登記所地図のハイライト
    const has2025Matches = matchedFeatures.some(m => m.source === '2025')
    if (has2025Matches) {
      highlightSpecificFeatures2025(map, 'oh-homusyo-2025-polygon')
    }

    // 各都市地番図のハイライト（レイヤー追加を待ってから実行）
    if (cityLayersToHighlight.size > 0) {
      // addLayers()がsetTimeout(0)で実行されるため、少し待つ
      setTimeout(() => {
        for (const cityLayerId of cityLayersToHighlight) {
          if (map.getLayer(cityLayerId)) {
            highlightSpecificFeaturesCity(map, cityLayerId)
          } else {
          }
        }
      }, 500)
    }

    // 10. 最初のfeatureにパン（詳細表示のため17）
    if (firstFeatureCenter) {
      map.flyTo({ center: firstFeatureCenter, zoom: 17, duration: 1000 })
    }

    // CSV出力
    const csvContent = toukiInfoToCsv(results)
    const timestamp = new Date().toISOString().slice(0, 10)
    downloadCsv(csvContent, `touki_${timestamp}.csv`)

    // 地番情報ウィンドウを開く
    vm.$store.dispatch('showFloatingWindow', 'chiban-info-window')

    // ダイアログを閉じて結果表示
    vm.toukiBatchDialog = false
    const totalChibans = successResults.length
    const matched2025 = matchedFeatures.filter(m => m.source === '2025').length
    const matchedCity = matchedFeatures.filter(m => m.source === 'city').length
    let message
    if (matchedFeatures.length > 0) {
      const details = []
      if (matched2025 > 0) details.push(`2025登記所地図: ${matched2025}筆`)
      if (matchedCity > 0) details.push(`各都市地番図: ${matchedCity}筆`)
      message = `${matchedFeatures.length}/${totalChibans}筆をハイライト + CSV出力しました (${details.join(', ')})`
    } else {
      // マッチしなかった場合、検索したレイヤーを表示
      const searchedDetails = []
      if (searchedLayers.has2025) searchedDetails.push('2025登記所地図')
      if (searchedLayers.cityNames.size > 0) {
        const cityNamesList = Array.from(searchedLayers.cityNames).join('、')
        searchedDetails.push(`${cityNamesList}地番図`)
      }
      if (searchedDetails.length > 0) {
        message = `一致する地番が見つかりませんでした\n検索したレイヤー: ${searchedDetails.join('、')}\n（CSVは出力済み）`
      } else {
        message = '一致する地番が見つかりませんでした（CSVは出力済み）'
      }
    }
    showInfo(message, { icon: 'mdi-map-marker-check' })


  } catch (error) {
    vm.toukiBatchDialog = false
    console.error('[ToukiAll] Error:', error)
    showError(`エラーが発生しました: ${error.message || error}`)
  }
}
